// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useProject } from '../../../../../contexts/ProjectContext';
import {
  FiCheckCircle as CheckCircle, FiXCircle as XCircle, FiClock as Clock,
  FiAlertTriangle as AlertTriangle, FiShield as Shield, FiFileText as FileText,
  FiDatabase as Database, FiSettings as Settings, FiCalendar as Calendar, FiUser as User,
  FiArrowRight as ArrowRight, FiLoader as Loader2, FiRefreshCw as RefreshCw, FiFilter as Filter,
  FiX as X
} from 'react-icons/fi';
import {
  getApprovals, getApprovalSummary, approveApproval, rejectApproval,
  getPrompt, getPromptVersions
} from '../../../../../services/aiOperationsApi';
import { StatusBadge, ResourceIcon, ProjectBadge, formatUserName } from '../GovernancePage';

const ApprovalPreviewModal = ({ isOpen, onClose, approval }) => {
  const [loading, setLoading] = useState(true);
  const [promptData, setPromptData] = useState(null);
  const [targetEnvPrompt, setTargetEnvPrompt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !approval) return;

    const loadPromptDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const sourcePrompt = await getPrompt(approval.resource_name, approval.version_number);
        setPromptData(sourcePrompt);

        try {
          const versions = await getPromptVersions(approval.resource_id);
          const targetVersion = versions?.data?.find(v => v.environment === approval.to_environment);
          if (targetVersion) {
            const targetPrompt = await getPrompt(approval.resource_name, targetVersion.version_number);
            setTargetEnvPrompt(targetPrompt);
          }
        } catch {
          setTargetEnvPrompt(null);
        }
      } catch (err) {
        console.error('Error loading prompt details:', err);
        setError(err.message || 'Failed to load prompt details');
      } finally {
        setLoading(false);
      }
    };

    loadPromptDetails();
  }, [isOpen, approval]);

  if (!isOpen) return null;

  let previewModalContent: React.ReactNode;
  if (loading) {
    previewModalContent = (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  } else if (error) {
    previewModalContent = (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-300">{error}</p>
      </div>
    );
  } else {
    previewModalContent = null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Approval Preview
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {approval?.resource_name} - v{approval?.version_number}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {previewModalContent ?? (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">From</span>
                      <p className="font-semibold text-gray-900 dark:text-white capitalize">
                        {approval?.from_environment}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-blue-600" />
                    <div className="text-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">To</span>
                      <p className="font-semibold text-blue-600 dark:text-blue-400 capitalize">
                        {approval?.to_environment}
                      </p>
                    </div>
                  </div>
                  {approval?.quality_gate_passed != null && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                      approval.quality_gate_passed
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}>
                      {approval.quality_gate_passed ? (
                        <><CheckCircle className="w-4 h-4" /> Quality Gate Passed</>
                      ) : (
                        <><XCircle className="w-4 h-4" /> Quality Gate Failed</>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Requested By</span>
                  <p className="font-medium text-gray-900 dark:text-white mt-1">{formatUserName(approval?.requested_by)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Requested</span>
                  <p className="font-medium text-gray-900 dark:text-white mt-1">
                    {new Date(approval?.requested_at).toLocaleString()}
                  </p>
                </div>
                {approval?.expires_at && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Expires</span>
                    <p className="font-medium text-gray-900 dark:text-white mt-1">
                      {new Date(approval.expires_at).toLocaleString()}
                    </p>
                  </div>
                )}
                {approval?.pass_rate != null && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Pass Rate</span>
                    <p className="font-medium text-gray-900 dark:text-white mt-1">
                      {(approval.pass_rate * 100).toFixed(1)}%
                    </p>
                  </div>
                )}
              </div>

              {approval?.release_notes && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Release Notes</span>
                  <p className="text-gray-900 dark:text-white mt-2">{approval.release_notes}</p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                  Prompt Content
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Current in {approval?.to_environment}
                      </span>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 max-h-64 overflow-y-auto">
                      {targetEnvPrompt ? (
                        <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                          {targetEnvPrompt.template}
                        </pre>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          No version currently deployed to {approval?.to_environment}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="border border-blue-200 dark:border-blue-700 rounded-lg overflow-hidden">
                    <div className="bg-blue-100 dark:bg-blue-900/50 px-4 py-2 border-b border-blue-200 dark:border-blue-600">
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        New Version (v{approval?.version_number})
                      </span>
                    </div>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 max-h-64 overflow-y-auto">
                      {promptData?.template ? (
                        <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                          {promptData.template}
                        </pre>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          Unable to load prompt content
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {promptData && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                    Metadata
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {promptData.description && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 md:col-span-3">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Description</span>
                        <p className="text-gray-900 dark:text-white mt-1">{promptData.description}</p>
                      </div>
                    )}
                    {(promptData.tags?.length ?? 0) > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Tags</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(typeof promptData.tags === 'string' ? JSON.parse(promptData.tags) : promptData.tags).map(tag => (
                            <span key={tag} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ApprovalCard = ({ approval, onApprove, onReject, projectName }) => {
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try { await onApprove(approval.id); } finally { setLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setLoading(true);
    try { await onReject(approval.id, rejectReason); setShowRejectModal(false); } finally { setLoading(false); }
  };

  const isPending = approval.status === 'pending';
  const daysUntilExpiry = approval.expires_at ? Math.ceil((new Date(approval.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;

  let approvalBorderClass: string;
  if (isPending) approvalBorderClass = 'border-yellow-500';
  else if (approval.status === 'approved') approvalBorderClass = 'border-green-500';
  else if (approval.status === 'rejected') approvalBorderClass = 'border-red-500';
  else approvalBorderClass = 'border-gray-300';

  let resourceTypeClass: string;
  if (approval.resource_type === 'prompt') resourceTypeClass = 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
  else if (approval.resource_type === 'dataset') resourceTypeClass = 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
  else resourceTypeClass = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';

  let expiryColorClass: string;
  if (daysUntilExpiry != null && daysUntilExpiry <= 1) expiryColorClass = 'text-red-600 dark:text-red-400';
  else if (daysUntilExpiry != null && daysUntilExpiry <= 3) expiryColorClass = 'text-amber-600 dark:text-amber-400';
  else expiryColorClass = 'text-gray-900 dark:text-white';

  return (
    <>
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 ${approvalBorderClass}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-lg ${resourceTypeClass}`}>
              <ResourceIcon type={approval.resource_type} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{approval.resource_name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {approval.from_environment} → {approval.to_environment}
                {approval.version_number && ` (v${approval.version_number})`}
              </p>
              <div className="mt-2">
                <ProjectBadge name={projectName} />
              </div>
            </div>
          </div>
          <StatusBadge status={approval.status} />
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Requested by</span>
            <p className="text-gray-900 dark:text-white font-medium flex items-center"><User className="w-3 h-3 mr-1" />{formatUserName(approval.requested_by)}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Requested</span>
            <p className="text-gray-900 dark:text-white font-medium flex items-center"><Calendar className="w-3 h-3 mr-1" />{new Date(approval.requested_at).toLocaleDateString()}</p>
          </div>
          {approval.quality_gate_passed != null && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Quality Gate</span>
              <p className={`font-medium flex items-center ${approval.quality_gate_passed ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {approval.quality_gate_passed ? <><CheckCircle className="w-3 h-3 mr-1" /> Passed</> : <><XCircle className="w-3 h-3 mr-1" /> Failed</>}
              </p>
            </div>
          )}
          {isPending && daysUntilExpiry != null && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Expires in</span>
              <p className={`font-medium ${expiryColorClass}`}>
                {daysUntilExpiry} day{daysUntilExpiry === 1 ? '' : 's'}
              </p>
            </div>
          )}
        </div>

        {approval.release_notes && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Release Notes</span>
            <p className="text-sm text-gray-900 dark:text-white mt-1">{approval.release_notes}</p>
          </div>
        )}

        {approval.rejection_reason && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <span className="text-xs text-red-600 dark:text-red-400 uppercase">Rejection Reason</span>
            <p className="text-sm text-red-800 dark:text-red-300 mt-1">{approval.rejection_reason}</p>
          </div>
        )}

        {isPending && (
          <div className="mt-4 flex items-center justify-end space-x-3">
            <button
              onClick={() => setShowPreviewModal(true)}
              className="px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg font-medium text-sm"
            >
              Preview
            </button>
            <button onClick={() => setShowRejectModal(true)} disabled={loading} className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium text-sm disabled:opacity-50">Reject</button>
            <button onClick={handleApprove} disabled={loading} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Approve
            </button>
          </div>
        )}
      </div>

      <ApprovalPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        approval={approval}
      />

      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Reject Approval Request</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Please provide a reason for rejecting this request.</p>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter rejection reason..." className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white resize-none" rows={4} />
            <div className="flex justify-end space-x-3 mt-4">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleReject} disabled={!rejectReason.trim() || loading} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ApprovalsContent = () => {
  const { projects } = useProject();
  const [approvals, setApprovals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [approvalsData, summaryData] = await Promise.all([
        getApprovals({ status: statusFilter, resource_type: resourceTypeFilter || undefined }),
        getApprovalSummary()
      ]);
      setApprovals(approvalsData);
      setSummary(summaryData);
    } catch (err) {
      setError(err.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [statusFilter, resourceTypeFilter]);

  const getCurrentUser = () => {
    return localStorage.getItem('userName') ||
           localStorage.getItem('userEmail') ||
           localStorage.getItem('user') ||
           'reviewer';
  };

  const handleApprove = async (requestId) => {
    try {
      const approval = approvals.find(a => a.id === requestId);
      await approveApproval(requestId, { reviewer_id: getCurrentUser(), approval_notes: 'Approved' });

      const resourceName = approval?.resource_name || 'Resource';
      const toEnv = approval?.to_environment || 'target environment';
      setSuccessMessage(`${resourceName} has been approved and automatically promoted to ${toEnv}`);

      setTimeout(() => setSuccessMessage(null), 5000);

      loadData();
    } catch (err) { setError(err.message); }
  };

  const handleReject = async (requestId, reason) => {
    try {
      await rejectApproval(requestId, { reviewer_id: getCurrentUser(), rejection_reason: reason });
      loadData();
    } catch (err) { setError(err.message); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;
  }

  return (
    <div>
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
            <div className="text-3xl font-bold text-yellow-800 dark:text-yellow-200">{summary.total_pending}</div>
            <div className="text-sm text-yellow-700 dark:text-yellow-300">Pending Approvals</div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="text-3xl font-bold text-blue-800 dark:text-blue-200">{summary.pending_by_type?.prompt_release || 0}</div>
            <div className="text-sm text-blue-700 dark:text-blue-300">Prompt Releases</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
            <div className="text-3xl font-bold text-red-800 dark:text-red-200">{summary.expiring_soon}</div>
            <div className="text-sm text-red-700 dark:text-red-300">Expiring Soon</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-200">{summary.oldest_pending_days || 0}</div>
            <div className="text-sm text-gray-700 dark:text-gray-300">Oldest Request (days)</div>
          </div>
        </div>
      )}

      <div className="flex items-center space-x-4 mb-6">
        <div className="flex items-center space-x-2"><Filter className="w-4 h-4 text-gray-500" /><span className="text-sm text-gray-600 dark:text-gray-400">Filters:</span></div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All Statuses</option>
        </select>
        <select value={resourceTypeFilter} onChange={(e) => setResourceTypeFilter(e.target.value)} className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm">
          <option value="">All Types</option>
          <option value="prompt">Prompts</option>
          <option value="dataset">Datasets</option>
          <option value="agent_config">Configs</option>
        </select>
        <button onClick={loadData} className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-center"><AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" /><p className="text-red-800 dark:text-red-300">{error}</p></div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
            <p className="text-green-800 dark:text-green-300">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {approvals.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-12 text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Approval Requests</h3>
          <p className="text-gray-600 dark:text-gray-400">{statusFilter === 'pending' ? 'There are no pending approval requests at this time.' : `No ${statusFilter} approval requests found.`}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {approvals.map((approval) => (
            <ApprovalCard
              key={approval.id}
              approval={approval}
              projectName={approval.project_id ? (projects.find((project) => project.id === approval.project_id)?.name ?? 'Project') : null}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalsContent;
